import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface GroupDto {
  ID: string;
  Name: string;
  Type: string;
  DCR_ID: string | null;
  Status: string;
  CreationTime?: string;
  LastUpdate?: string;
  Items?: Array<{ DCR_ID: string; Name: string }>;
  DCR_IDs?: string[];
}

interface GenericResponse<T> {
  payload?: T;
  error?: {
    status: number;
    message: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class GroupsApiService {
  private readonly fallbackBase = this.normalizeBase(environment.apiUrl);

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  isTNCIDPlusMember(): Observable<boolean> {
    const endpoint = this.composeEndpoint('/webapp/crud/groups/is-tncidplus-member');
    return this.http.get<GenericResponse<{ isTNCIDPlusMember: boolean }>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => res.payload?.isTNCIDPlusMember ?? false),
      catchError(() => [false])
    );
  }

  getGroups(): Observable<GroupDto[]> {
    const endpoint = this.composeEndpoint('/webapp/crud/groups');
    return this.http.get<GenericResponse<GroupDto[]>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => res.payload ?? []),
      catchError(error => {
        console.error('[groups-api] list failed', error);
        return throwError(() => error);
      })
    );
  }

  private composeEndpoint(path: string): { url: string; useRemote: boolean } {
    const info = this.getBaseUrlInfo();
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
    return {
      url: `${info.baseUrl}${path}`,
      useRemote: info.useRemote
    };
  }

  private buildHeaders(useRemote: boolean): HttpHeaders {
    if (useRemote) {
      let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      const token = this.authService.getTncApiToken();
      if (token) {
        headers = headers.set('Authorization', token);
      }
      return headers;
    }
    let headers = new HttpHeaders(this.authService.getAuthHeaders());
    headers = headers.set('Content-Type', 'application/json');
    return headers;
  }

  private getBaseUrlInfo(): { baseUrl: string; useRemote: boolean } {
    const remoteBase = this.authService.getTncApiBaseUrl();
    const remoteToken = this.authService.getTncApiToken();
    if (remoteBase && remoteToken) {
      return { baseUrl: this.normalizeBase(remoteBase), useRemote: true };
    }
    return { baseUrl: this.fallbackBase, useRemote: false };
  }

  private normalizeBase(url: string): string {
    if (!url) {
      return '';
    }
    return url.replace(/\/+$/u, '');
  }
}
